import { US_LETTER_SCALE_WIDTH_PX } from '@/modules/presentation/PresentationSlide/PresentationSlide.constants';

export {
  CoverSlideConfigBodyTextAlignment,
  CoverSlideConfigTitleTextSize,
  type PresentationCoverSlideConfig,
  SlideConfigVariant,
} from '@/modules/slideEditor/coverSlide/CoverSlide.types';
import {
  CoverSlideConfigBodyTextAlignment,
  CoverSlideConfigTitleTextSize,
} from '@/modules/slideEditor/coverSlide/CoverSlide.types';

/**
 * Form shape for the cover/divider slide config tabs.
 * Mirrors the backend PresentationCoverSlideConfig entity fields.
 */
export interface SlideConfigFormShape {
  backgroundColor: string;
  useDarkLogo: boolean;
  footerColor: string;
  backgroundGradientEndColor: string;
  backgroundGradientAngle: string;
  backgroundOverlayPath: string;
  backgroundOverlayAlpha: string;
  backgroundOverlayPercentScale: string;
  backgroundOverlayOriginX: string;
  backgroundOverlayOriginY: string;
  textColor: string;
  bodyTextColor: string;
  /**
   * Body text alignment for the slide's markdown content. Defaults to
   * left-aligned. Applied to the rendered body via the tenant slide
   * design → SlideEditorRichTextEditor → Lexical theme.
   */
  bodyTextAlignment: CoverSlideConfigBodyTextAlignment;
  backgroundImageUrl: string;
  /**
   * Stored as a string ('0'..'100' or '') rather than a number so a
   * value of 0 isn't coerced to empty by the shared percent-input
   * value helper (which treats `0` as falsy).
   */
  backgroundAlpha: string;
  /**
   * Continuous logo scale (1–200) replacing the legacy 5-bucket
   * logoSize enum. String storage (mirrors backgroundAlpha) so the
   * empty case can stay distinct from a valid 100. Empty → 100% at
   * render time.
   */
  logoPercentScale: string;
  /**
   * Whether the logo follows the title block ("default position",
   * checked) or is positioned independently via drag (unchecked).
   * The legacy 5-corner logoPosition enum is gone — only these two
   * states are exposed.
   */
  useDefaultLogoPosition: boolean;
  /**
   * Drag-driven logo offsets, in slide-coordinate pixels, applied as
   * a CSS `transform: translate(...)`. Only consumed when
   * `useDefaultLogoPosition` is false.
   */
  logoOriginX: number | null;
  logoOriginY: number | null;
  /**
   * Inverse of the BE `hideLogo` flag — exposed as a positive
   * "Show logo" checkbox in the form. When false, the logo is omitted
   * from the slide entirely.
   */
  showLogo: boolean;
  titleTextSize: CoverSlideConfigTitleTextSize;
  /**
   * Continuous title size in pt (T1-5972) — the unit users see in the
   * admin form. String storage mirrors `logoPercentScale` so '' is
   * distinguishable from a valid value and means "not set" — the slide
   * then falls back to the legacy `titleTextSize` lookup at render time.
   * The BE field is in rem; conversion happens at the form↔mutation
   * boundary (see `toFormValues` / `toMutationInput`).
   */
  titleSizePt: string;
  includeFooter: boolean;
  /**
   * Single "Use all selections from cover slide" checkbox (divider only).
   * When true, the divider uses every visual config field from the cover
   * slide, ignoring its own. Cover slides ignore this field.
   */
  inheritCover: boolean;
  /**
   * Hidden origin fields driven by drag-and-drop in the slide preview.
   * Stored as integer pixels in slide-coordinate space (the unscaled
   * canvas — see ScalableSlide). `null` means "no offset from default";
   * `null`, `undefined`, and missing all collapse to `0` at render time.
   */
  backgroundImageOriginX: number | null;
  backgroundImageOriginY: number | null;
  textOriginX: number | null;
  textOriginY: number | null;
  /**
   * Width of the text block in slide-coordinate pixels, stored as a
   * string (mirrors `logoPercentScale`) so '' can mean "not set". When
   * empty, the text block stretches to the opposite slide padding.
   */
  textWidth: string;
  /**
   * Background image scale, expressed as a 0-100-style percentage string
   * (mirrors `backgroundAlpha`'s storage convention). Valid range is
   * 50–150. Empty string means "not set" and the slide renders at 100%.
   */
  backgroundImagePercentScale: string;
}

export const LOGO_SCALE_MIN = 1;

/**
 * Default horizontal inset of the slide text block, in slide-coordinate
 * pixels: `theme.spacing(10.5)` at the 8px spacing unit.
 */
export const SLIDE_TEXT_HORIZONTAL_INSET_PX = 84;

/** Narrowest text block width the admin can set, in slide pixels. */
export const TEXT_WIDTH_PX_MIN = 200;
/**
 * Widest text block the admin can set: the full slide canvas minus the
 * default inset on each side. This is also the width of an unset block
 * that has no horizontal offset.
 */
export const TEXT_WIDTH_PX_MAX =
  US_LETTER_SCALE_WIDTH_PX - 2 * SLIDE_TEXT_HORIZONTAL_INSET_PX;
export const LOGO_SCALE_MAX = 200;

/**
 * CSS conversion factor: 1rem (16px) = 12pt at the browser default
 * 16px root font size. The admin form stores pt; the BE stores rem.
 */
export const PT_PER_REM = 12;

/** Inclusive bounds for the slider that drives `titleSizePt` (T1-5972). */
export const TITLE_SIZE_PT_MIN = 0.1 * PT_PER_REM; // 1.2 pt = 0.1 rem
export const TITLE_SIZE_PT_MAX = 5.5 * PT_PER_REM; // 66 pt = 5.5 rem
export const TITLE_SIZE_PT_STEP = 0.1;
/**
 * Slider fallback value when no `titleSizePt` is saved — matches the
 * font-size of the legacy `Medium` bucket (2.25rem = 27pt) so dragging
 * away from the default starts at the same visual size users get today.
 */
export const TITLE_SIZE_PT_FALLBACK = 2.25 * PT_PER_REM; // 27 pt
